import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  CircularProgress,
} from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import moment from "moment-timezone";
import { formatCurrency } from "../../../utils/format/currency";
import { nowOperationalDateString } from "../../../utils/operationalDateTime";

interface ModalOfficeExpenseProps {
  open: boolean;
  onClose: () => void;
  showAlert: (message: string, severity?: "success" | "info" | "warning" | "error") => void;
  showLoading: () => void;
  hideLoading: () => void;
  clientData?: {
    boxId: number;
    date: string;
    dateISO?: string;
    out?: boolean;
    collect: string;
    collectId?: number | string;
    typeOperation: number;
  } | null;
}

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 1000,
  height: 600,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const ModalOfficeExpense: React.FC<ModalOfficeExpenseProps> = ({
  open,
  onClose,
  clientData,
  showAlert,
}) => {
  const [movement, setMovement] = useState<any[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const showAlertRef = useRef(showAlert);
  showAlertRef.current = showAlert;

  /** Una fila de Resumen Día = un día operativo; alineado con consolidado (date_start === date_end). */
  const dateNorm = useMemo(() => {
    if (!clientData) return "";
    const m = moment(
      clientData.dateISO || clientData.date,
      ["YYYY-MM-DD", "YYYY/MM/DD", moment.ISO_8601],
      true
    );
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
  }, [clientData?.dateISO, clientData?.date]);

  const clientKey = useMemo(() => {
    if (!clientData || !dateNorm) return "";
    return [
      clientData.boxId,
      dateNorm,
      String(clientData.collectId ?? ""),
      clientData.typeOperation,
      clientData.out ?? false,
    ].join("|");
  }, [clientData?.boxId, dateNorm, clientData?.collectId, clientData?.typeOperation, clientData?.out]);

  useEffect(() => {
    if (!open) {
      setMovement([]);
      setIsFetching(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !clientData || !clientKey || !dateNorm) return;

    const ac = new AbortController();
    setIsFetching(true);
    setMovement([]);

    const fetchData = async () => {
      try {
        let records: any[] = [];
        const singleDay = true;

        if (clientData.typeOperation === 3 && singleDay) {
          const numericCollectId = Number(clientData.collectId);
          if (!Number.isFinite(numericCollectId) || numericCollectId <= 0) {
            showAlertRef.current("No se pudo identificar el cobro seleccionado.", "warning");
            setMovement([]);
            return;
          }
          const { data } = await axios.post(
            "/process/findexpensesbox",
            { date: dateNorm, collect_id: numericCollectId },
            { signal: ac.signal }
          );
          if (Array.isArray(data)) {
            records = Array.isArray(data[0]) ? (data[0] ?? []) : data;
          } else if (Array.isArray(data?.data)) {
            records = data.data;
          }
        } else {
          const formData = {
            boxId: clientData.boxId,
            date_start: dateNorm,
            date_end: dateNorm,
            collect_id: String(clientData.collectId ?? ""),
            type_operation: clientData.typeOperation,
            out: clientData.out ?? false,
          };
          const { data } = await axios.post("/report/modal/expenseconsolidate", formData, {
            signal: ac.signal,
          });
          records = Array.isArray(data?.data) ? data.data : [];
        }

        if (ac.signal.aborted) return;

        const filtered = records.filter(Boolean);
        setMovement(filtered);
        if (filtered.length === 0) {
          showAlertRef.current("No se encontraron registros para esta consulta.", "info");
        } else {
          showAlertRef.current(
            `Se encontraron ${filtered.length} registro${filtered.length === 1 ? "" : "s"}.`,
            "success"
          );
        }
      } catch (error: unknown) {
        if (ac.signal.aborted) return;
        console.error("Error al cargar datos del modal:", error);
        showAlertRef.current("Error al cargar los gastos.", "error");
        setMovement([]);
      } finally {
        if (!ac.signal.aborted) {
          setIsFetching(false);
        }
      }
    };

    fetchData();

    return () => {
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientKey]);

  const rows = useMemo(() => {
    const parsed = (Array.isArray(movement) ? movement : []).map((data) => {
      const rawDateTime = data?.createdAt || data?.created_at || data?.date || null;
      const dateMoment = rawDateTime ? moment.tz(rawDateTime, "America/Bogota") : null;
      const fallbackDate = data?.date ? moment(data.date).format("YYYY/MM/DD") : "";
      return {
        ...data,
        id: data?.id ?? `${String(rawDateTime || "")}-${String(data?.description || "")}-${Math.random()}`,
        date: dateMoment?.isValid() ? dateMoment.format("YYYY/MM/DD") : fallbackDate,
        hour: dateMoment?.isValid() ? dateMoment.format("HH:mm") : "",
        value: Number(data?.value) || 0,
        type: data?.expense?.name || data?.type || "",
        description: data?.description || "",
      };
    });

    const totalValue = parsed.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
    return [
      ...parsed.map((r) => ({ ...r, value_fmt: formatCurrency(r.value) })),
      {
        id: "total-row",
        date: "TOTAL",
        hour: "",
        value: totalValue,
        value_fmt: formatCurrency(totalValue),
        type: "",
        description: "",
      },
    ];
  }, [movement]);

  const columns: GridColDef[] = [
    { field: "date", headerName: "Fecha", flex: 1 },
    { field: "hour", headerName: "Hora", flex: 1 },
    { field: "value_fmt", headerName: "Valor", flex: 1 },
    { field: "type", headerName: "Tipo", flex: 1 },
    { field: "description", headerName: "Descripción", flex: 1 },
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Paper sx={modalStyle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <Typography variant="h6">Información Gastos Resumen Día</Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }} aria-label="cerrar">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, overflowY: "auto", flexGrow: 1 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Movimientos Realizados -{" "}
            {clientData?.typeOperation === 3 ? "LA RUTA" : clientData?.collect || "Cobro"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6">
                <strong>Fecha:</strong> {dateNorm || clientData?.date || "—"}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ position: "relative", minHeight: 300, width: "100%" }}>
            {isFetching && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.75)",
                  zIndex: 2,
                  borderRadius: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id || Math.random()}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  csvOptions: {
                    fileName: `resumen-${nowOperationalDateString()}`,
                    utf8WithBom: true,
                  },
                  printOptions: { disableToolbarButton: true },
                  showQuickFilter: true,
                },
              }}
              hideFooter
              autoHeight
              density="compact"
              disableColumnFilter
              disableColumnMenu
              disableRowSelectionOnClick
              disableColumnSelector
              disableDensitySelector
              disableVirtualization
              sx={{
                "& .MuiDataGrid-root": { border: "1px solid #1976d2" },
                "& .MuiDataGrid-toolbarContainer": {
                  border: "1px solid #1976d2",
                  backgroundColor: "#f5faff",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#1976d2",
                  "& .MuiDataGrid-columnHeader": {
                    borderRight: "1px solid #1976d2",
                    "&:last-child": { borderRight: "none" },
                  },
                },
                "& .MuiDataGrid-cell": {
                  borderRight: "1px solid #1976d2",
                  borderLeft: "2px solid #1976d2",
                  borderBottom: "1px solid #1976d2",
                  "&:last-child": { borderRight: "none" },
                },
                "& .MuiDataGrid-row": {
                  borderBottom: "1px solid #1976d2",
                  "&:last-child": {
                    borderBottom: "1px solid #1976d2",
                    backgroundColor: "#fafafa",
                    borderTop: "2px solid #1976d2",
                  },
                },
                "& .header-style": {
                  fontWeight: "bold",
                  color: "#212121",
                },
                "& .MuiDataGrid-columnSeparator": { display: "none" },
                "& .footer-total .MuiDataGrid-cell": {
                  justifyContent: "center",
                  backgroundColor: "#e3f2fd",
                  textAlign: "center",
                  fontWeight: "bold",
                },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 2,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Box>
            <Button variant="outlined" onClick={onClose} sx={{ mr: 1 }}>
              Cerrar
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default ModalOfficeExpense;
